/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRightIcon, FileIcon, Trash2Icon } from "lucide-react";
import {
  FieldPath,
  useFieldArray,
  useForm,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { DisabledExplanationTooltip } from "@/components/DisabledExplanationTooltip";
import { EmptyDataDash } from "@/components/EmptyDataDash";
import { EmptyState } from "@/components/EmptyState";
import { Row } from "@/components/Row";
import { StickyTable } from "@/components/StickyTable";
import { TableCellCurrencyInput } from "@/components/TableCellCurrencyInput";
import { TableCellDatePicker } from "@/components/TableCellDatePicker";
import { TableCellEnumSelect } from "@/components/TableCellEnumSelect";
import { TableCellNumberInput } from "@/components/TableCellNumberInput";
import { TableCellNumberSelect } from "@/components/TableCellNumberSelect";
import { TableCellTextInput } from "@/components/TableCellTextInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { mergeUserOptionsWithSelected, useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useDeletePlacement } from "@/hooks/mutations/useDeletePlacement";
import { useUpdatePlacement } from "@/hooks/mutations/useUpdatePlacement";
import { useRemovePlacement } from "@/hooks/sets/useRemovePlacement";
import { useSetPlacement } from "@/hooks/sets/useSetPlacement";
import { useSetRequisition } from "@/hooks/sets/useSetRequisition";
import {
  defaultPlacementQueryParams,
  placementStatusToDotColor,
  placementStatusToLabel,
  requisitionStatusToDotColor,
  requisitionStatusToLabel,
  shiftTypeOptions,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { misc } from "@/lib/misc";
import { mutations } from "@/lib/mutations";
import { nullableDateSchema, requiredDateSchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";
import {
  CandidacyPlacement,
  CompensationType,
  Job,
  PlacementQueryParams,
  PlacementStatus,
  RequisitionStatus,
  RevenueType,
  ShiftType,
} from "@/types";

// * IMPORTANT: All of these schema names that correspond with EDITABLE fields need to stay consistent with the column names in the placement table.
// * IMPORTANT: We are indexing the requisition object by field name of the form to compare values in handleOnBlur() and sending fieldName directly to the BE for updates.
const placementSchema = z.object({
  backfillGuaranteeDays: z.coerce.number().min(0).nullable(),
  baseCompensation: z.coerce.number().min(1),
  candidacyId: z.string(),
  candidateFirstName: z.string(),
  candidateLastName: z.string(),
  dateAccepted: nullableDateSchema,
  dateOffered: requiredDateSchema,
  dateEmploymentEnded: nullableDateSchema,
  dateRejected: nullableDateSchema,
  dateSetToStart: nullableDateSchema,
  dateStarted: nullableDateSchema,
  retentionBonusDays: z.coerce.number().min(0).nullable(),
  employmentEndedReason: z.string().nullable(),
  id: z.number(),
  ownerUserId: z.coerce.number(),
  placementStatus: z.nativeEnum(PlacementStatus),
  requisitionId: z.number(),
  requisitionStatus: z.nativeEnum(RequisitionStatus),
  retentionBonusAmount: z.coerce.number().min(0).nullable(),
  revenueEarned: z.coerce.number().min(0).nullable(),
  shiftDifferential: z.coerce.number().min(0).nullable(),
  shiftType: z.nativeEnum(ShiftType),
  shiftTypeDescription: z.string().nullable(),
});

const schema = z.object({
  placements: z.array(placementSchema),
});

const showInlineError = (
  form: UseFormReturn<z.infer<typeof schema>>,
  fieldName: FieldPath<z.infer<typeof schema>>,
) => {
  return form.getFieldState(fieldName).error ? "bg-red-100" : "";
};

// Format dates for display and calculate days fields
const placementToSchema = (placement: CandidacyPlacement, job: Job) => {
  return {
    ...placement,
    candidacyId: placement.candidacy.id,
    candidateFirstName: placement.candidacy.firstName ?? "",
    candidateLastName: placement.candidacy.lastName ?? "",
    dateAccepted: dates.formatIsoDateForUser(placement.dateAccepted),
    dateEmploymentEnded: dates.formatIsoDateForUser(
      placement.dateEmploymentEnded,
    ),
    dateOffered: dates.formatIsoDateForUser(placement.dateOffered) ?? "",
    dateRejected: dates.formatIsoDateForUser(placement.dateRejected),
    dateSetToStart: dates.formatIsoDateForUser(placement.dateSetToStart),
    dateStarted: dates.formatIsoDateForUser(placement.dateStarted),
    backfillGuaranteeDays: job.backfillGuaranteeDays,
    placementStatus: placement.status,
    requisitionId: placement.requisition.id,
    requisitionStatus: placement.requisition.status,
  };
};

export const PlacementTabTable = ({
  isCandidateSheet,
  job,
  placementQueryParams,
  placements,
}: {
  isCandidateSheet?: boolean;
  job: Job;
  placementQueryParams?: PlacementQueryParams;
  placements: CandidacyPlacement[];
}) => {
  const companyId = useCurrentCompanyId();
  const jobId = useCurrentJobId();

  /* Contexts */
  const { userNameMap, userOptions: activeUserOptions } = useTaggableUsers();

  /* UseStates */
  const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] =
    useState(false);
  const [placementToDelete, setPlacementToDelete] = useState<z.infer<
    typeof placementSchema
  > | null>(null);

  /* Database CRUD Operations */
  // Update
  const { mutate: updatePlacement } = useUpdatePlacement();

  // Delete
  const { mutate: deletePlacement } = useDeletePlacement();

  /* Cache Updates */
  // Sets
  const setPlacement = useSetPlacement();
  const setRequisition = useSetRequisition();

  // Removes
  const removePlacement = useRemovePlacement();

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { placements: [] },
  });
  const { fields } = useFieldArray({
    control: form.control,
    name: "placements",
    // Set React Hook Form's generated key to "placementId" instead of defaulted "id" so Laborup "id" column can be referenced from the form for updates/deletes
    keyName: "placementId",
  });

  const watchedPlacements = form.watch("placements");

  const handleOnBlur = (
    id: number,
    fieldName: string,
    updatedValue: string | null,
    type?: "date" | "number",
  ) => {
    const valueToUpdate = misc.handleFormFieldToDatabaseValue(
      updatedValue,
      type,
    );

    mutations.updatePlacement({
      placementId: id,
      setPlacement,
      setRequisition,
      updatePlacement,
      updatePlacementRequest: { [fieldName]: valueToUpdate },
    });

    // If base compensation is updated and this job is percent revenue type, update the cache to show revenue earned changed
    if (
      fieldName === "baseCompensation" &&
      job.revenueType === RevenueType.PERCENT
    ) {
      const placementIndex = form
        .getValues("placements")
        .findIndex((p) => p.id === id);
      if (placementIndex !== -1) {
        const revenueEarned = misc.calculateEstimatedRevenueAmount(
          job.compensationType === CompensationType.HOURLY,
          Number(valueToUpdate),
          Number(valueToUpdate),
          job.percentOfSalary ?? 0,
        );
        form.setValue(
          `placements.${placementIndex}.revenueEarned`,
          revenueEarned || null,
        );
      }
    }
  };

  const handleDeletePlacement = (index: number) => {
    const placement = form.getValues(`placements.${index}`);
    setPlacementToDelete(placement);
    setIsDeleteConfirmationDialogOpen(true);
  };

  const handleDeletePlacementConfirmation = () => {
    if (!placementToDelete) return;
    deletePlacement(
      { placementId: placementToDelete.id },
      {
        onSuccess: (deletedPlacement) => {
          setIsDeleteConfirmationDialogOpen(false);
          removePlacement({
            id: deletedPlacement.id,
            placementQueryParams: defaultPlacementQueryParams,
          });
          setPlacementToDelete(null);
          form.setValue(
            "placements",
            form
              .getValues("placements")
              .filter((p) => p.id !== deletedPlacement.id),
          );
        },
      },
    );
  };

  // Populate form with the placements data
  useEffect(() => {
    // Don't want the form to reset if a field within a placement is updated. It doesn't allow focus to shift from one field (updated) to another. Rerender loses the focus.
    // However, if placement status updates due to PlacementDialog, EmployedDialog, or EmploymentEndedDialog,
    // we want to reset the form so the new status (and maybe requisition status) is reflected in the table.
    if (
      placements.length !== form.getValues("placements").length ||
      (placements.length > 0 &&
        placements[0].status !==
          form.getValues("placements")[0].placementStatus)
    ) {
      form.reset({
        placements: placements.map((p) => placementToSchema(p, job)),
      });
    }
  }, [form, job, placements]);

  const listName = "placements";

  return (
    <Form {...form}>
      <form>
        <StickyTable hasRows={fields.length > 0}>
          <TableHeader className="sticky top-0 z-10 bg-slate-50 [&_tr]:border-b-0">
            <TableRow className="sticky-border-bottom">
              <TableHead>Req ID</TableHead>
              <TableHead>Requisition Status</TableHead>
              <TableHead>Placement Status</TableHead>
              {!isCandidateSheet && <TableHead>Candidate</TableHead>}
              <TableHead>Owner</TableHead>
              <TableHead>Shift Type</TableHead>
              <TableHead>Shift Type Description</TableHead>
              <TableHead>Shift Differential ($/hr)</TableHead>
              <TableHead>
                Base Compensation ($
                {job.compensationType === CompensationType.HOURLY
                  ? "/hr"
                  : "/yr"}
                )
              </TableHead>
              <TableHead>
                Revenue Earned (
                {job.revenueType === RevenueType.PERCENT
                  ? `${job.percentOfSalary ?? 0}% of annualized salary`
                  : "fixed"}
                )
              </TableHead>
              <TableHead>Date Offered</TableHead>
              <TableHead>Date Set to Start</TableHead>
              <TableHead>Date Accepted</TableHead>
              <TableHead>Date Rejected</TableHead>
              <TableHead>Backfill Guarantee Days</TableHead>
              <TableHead>Retention Bonus ($)</TableHead>
              <TableHead>Retention Bonus Days</TableHead>
              <TableHead>Date Started</TableHead>
              <TableHead>Date Employment Ended</TableHead>
              <TableHead>Employment Ended Reason</TableHead>
              <TableHead className="sticky-border-left sticky right-0 bg-slate-50">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.length > 0 ? (
              fields.map((row, index) => {
                const placement = form.getValues(`placements.${index}`);
                const rowOwnerOptions = mergeUserOptionsWithSelected(
                  activeUserOptions,
                  userNameMap,
                  [
                    watchedPlacements[index]?.ownerUserId ??
                      placement.ownerUserId,
                  ],
                );
                return (
                  <TableRow key={row.placementId} className="hover:shadow-sm">
                    <TableCell className="whitespace-nowrap">
                      {
                        placements.find((p) => p.id === row.id)?.requisition
                          .externalReqId
                      }
                    </TableCell>
                    <TableCellDisabledEnumPicker
                      enumToDotColor={requisitionStatusToDotColor}
                      enumToLabel={requisitionStatusToLabel}
                      value={placement.requisitionStatus}
                    />
                    <TableCellDisabledEnumPicker
                      enumToDotColor={placementStatusToDotColor}
                      enumToLabel={placementStatusToLabel}
                      value={placement.placementStatus}
                    />
                    {!isCandidateSheet && (
                      <TableCell
                        className="cursor-pointer"
                        onClick={() => {
                          mixpanelUtils.trackRowClick(
                            "Placement candidate",
                            getScreenFromPathname(location.pathname),
                          );
                          window.open(
                            `/companies/${companyId}/jobs/${jobId}?c=${placement.candidacyId}`,
                            "_blank",
                          );
                        }}
                      >
                        <Row className="items-center gap-2">
                          {`${placement.candidateFirstName} ${placement.candidateLastName}`.trim() || (
                            <EmptyDataDash />
                          )}
                          <ArrowUpRightIcon className="h-4 w-4" />
                        </Row>
                      </TableCell>
                    )}
                    <TableCellNumberSelect
                      className="w-60"
                      fieldName="ownerUserId"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      options={rowOwnerOptions}
                      rowId={placement.id}
                    />
                    <TableCellEnumSelect
                      className="w-36"
                      fieldName="shiftType"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      options={shiftTypeOptions}
                      rowId={placement.id}
                    />
                    <TableCellTextInput
                      fieldName="shiftTypeDescription"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                    />
                    <TableCellCurrencyInput
                      disabled={
                        job.compensationType === CompensationType.YEARLY
                      }
                      fieldName="shiftDifferential"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                    />
                    <TableCellCurrencyInput
                      fieldName="baseCompensation"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellCurrencyInput
                      disabled={job.revenueType === RevenueType.PERCENT}
                      fieldName="revenueEarned"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateOffered"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      fieldName="dateSetToStart"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateAccepted"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateRejected"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellNumberInput
                      disabled
                      fieldName="backfillGuaranteeDays"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellCurrencyInput
                      fieldName="retentionBonusAmount"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellNumberInput
                      fieldName="retentionBonusDays"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateStarted"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellDatePicker
                      disabled
                      fieldName="dateEmploymentEnded"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                      showError={(form, fieldName) =>
                        showInlineError(
                          form,
                          fieldName as FieldPath<z.infer<typeof schema>>,
                        )
                      }
                    />
                    <TableCellTextInput
                      disabled
                      fieldName="employmentEndedReason"
                      form={form}
                      handleOnBlur={handleOnBlur}
                      index={index}
                      listName={listName}
                      rowId={placement.id}
                    />
                    <TableCell className="sticky-border-left sticky right-0 bg-slate-50">
                      <DisabledExplanationTooltip
                        disabled={
                          placement.placementStatus !== PlacementStatus.OFFERED
                        }
                        message="Placements can only be deleted in the Offered state."
                        side="left"
                      >
                        <Button
                          disabled={
                            placement.placementStatus !==
                            PlacementStatus.OFFERED
                          }
                          onClick={() => {
                            handleDeletePlacement(index);
                          }}
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4"
                        >
                          <Trash2Icon />
                        </Button>
                      </DisabledExplanationTooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell className="p-0" colSpan={25}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description={`No placements were found. ${placementQueryParams && placementQueryParams.placementStatuses.length > 0 ? `Try clearing the filters to see placements for this job.` : ``}`}
                    Icon={FileIcon}
                    title="No placements found"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StickyTable>
        <ConfirmationDialog
          confirmationButtonVariant="destructive"
          confirmButtonText="Delete"
          description={`Are you sure you want to delete placement ${placementToDelete?.id}? This action cannot be undone.`}
          onConfirm={handleDeletePlacementConfirmation}
          onOpenChange={setIsDeleteConfirmationDialogOpen}
          open={isDeleteConfirmationDialogOpen}
          title="Delete Placement"
        />
      </form>
    </Form>
  );
};

const TableCellDisabledEnumPicker = ({
  enumToDotColor,
  enumToLabel,
  value,
}: {
  enumToDotColor: Record<string, string>;
  enumToLabel: Record<string, string>;
  value: string;
}) => {
  return (
    <TableCell>
      <Badge className="gap-2 text-sm" variant="outline">
        <div className={cn("h-2 w-2 rounded-full", enumToDotColor[value])} />
        <p>{enumToLabel[value]}</p>
      </Badge>
    </TableCell>
  );
};
